import * as React from "react";
import { VariableSizeList, type ListChildComponentProps } from "react-window";

type VirtualizedOptionElement = React.ReactElement<{ style?: React.CSSProperties }>;

const OuterElementContext = React.createContext<Record<string, any>>({});
const InnerElementContext = React.createContext<{
  itemData: VirtualizedOptionElement[];
  itemCount: number;
}>({
  itemData: [],
  itemCount: 0,
});

const OuterElementType = React.forwardRef<HTMLDivElement, any>((props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  const { ownerState, ...outerPropsWithoutOwnerState } = outerProps ?? {};

  return (
    <div
      ref={ref}
      {...outerPropsWithoutOwnerState}
      {...props}
      style={{ ...(outerPropsWithoutOwnerState?.style ?? {}), ...(props?.style ?? {}) }}
    />
  );
});
OuterElementType.displayName = "OuterElementType";

const InnerElementType = React.forwardRef<HTMLUListElement, React.HTMLAttributes<HTMLUListElement>>(
  (props, ref) => {
    const { itemData, itemCount } = React.useContext(InnerElementContext);
    const firstItem = itemData[0];
    const lastItem = itemData[itemCount - 1];

    return (
      <ul ref={ref} {...props}>
        {firstItem
          ? React.cloneElement(firstItem, {
              style: { position: "absolute", top: 0, left: 0, width: "100%", height: ITEM_SIZE },
            })
          : null}
        {props.children}
        {itemCount > 1 && lastItem
          ? React.cloneElement(lastItem, {
              style: {
                position: "absolute",
                top: (itemCount - 1) * ITEM_SIZE,
                left: 0,
                width: "100%",
                height: ITEM_SIZE,
              },
            })
          : null}
      </ul>
    );
  },
);
InnerElementType.displayName = "InnerElementType";

type ItemData = {
  itemData: VirtualizedOptionElement[];
  itemCount: number;
};

function renderRow({ data, index, style }: ListChildComponentProps<ItemData>) {
  if (index === 0 || index === data.itemCount - 1) {
    return null;
  }

  return React.cloneElement(data.itemData[index], { style });
}

const ITEM_SIZE = 36;

export const VirtualizedListboxComponent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLElement>
>(function VirtualizedListboxComponent(props, ref) {
  const { children, ...other } = props;

  const itemData = React.Children.toArray(children) as VirtualizedOptionElement[];
  const itemCount = itemData.length;
  const virtualizedItemData = React.useMemo(() => ({ itemData, itemCount }), [itemData, itemCount]);

  const maxHeightPx =
    typeof other.style?.maxHeight === "number"
      ? other.style.maxHeight
      : typeof other.style?.maxHeight === "string" && other.style.maxHeight.endsWith("px")
        ? parseInt(other.style.maxHeight, 10)
        : undefined;

  const defaultHeight = Math.min(itemCount, 8) * ITEM_SIZE;
  const height = maxHeightPx ? Math.min(defaultHeight, maxHeightPx) : defaultHeight;

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <InnerElementContext.Provider value={{ itemData, itemCount }}>
          <VariableSizeList
            itemData={virtualizedItemData}
            itemCount={itemCount}
            itemSize={() => ITEM_SIZE}
            height={height}
            width="100%"
            outerElementType={OuterElementType}
            innerElementType={InnerElementType}
            overscanCount={5}
          >
            {renderRow}
          </VariableSizeList>
        </InnerElementContext.Provider>
      </OuterElementContext.Provider>
    </div>
  );
});
